import { Button } from "@/components/Buttons";
import styles from "./css/AlternativesPanel.module.css";
import { AlternativeCard } from "@/components/cards/AlternativeCard";
import { BuildingsIcon } from "@/icons/BuildingsIcon";
import { AlternativeData } from "@/types/alternative";
import { useEffect, useMemo, useState } from "react";
import { OfficeData } from "@/types";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface AlternativesPanelProps {
    alternatives: AlternativeData[]
    userAo: OfficeData | null
    selectedAlternativeId: string | null;
    onAlternativeSelect: (alternative: AlternativeData) => void;
    favoriteId: string | null;
    onFavoriteSelect: (id: string, isFavorite: boolean) => void;
    checkedAlternatives: Set<AlternativeData>;
    onCheck: (alternative: AlternativeData, checked: boolean) => void;
    onCompareAlternatives: () => void;
    isCompare: boolean;
    setIsCompare: (isCompare: boolean) => void;
    bestAlternative: AlternativeData;
}

export const AlternativesPanel = ({alternatives, userAo, selectedAlternativeId, onAlternativeSelect, favoriteId, onFavoriteSelect,checkedAlternatives, onCheck, onCompareAlternatives, isCompare, setIsCompare, bestAlternative}: AlternativesPanelProps) => {
      const { t } = useTranslation('AlternativesPanel');
      const navigate = useNavigate();
      const [isNumberOfAlternativesValid, setIsNumberOfAlternativesValid] = useState(true);
      const {entityName, projectId, cityId} = useParams<{entityName: string, projectId: string, cityId: string}>();

    const setFavourite = (id: string, isFavourite: boolean) => {
        onFavoriteSelect(id, isFavourite);
    };

    const sortedAlternatives = useMemo(() => {
        const favorite = alternatives.find(alt => alt.id === favoriteId);
        const others = alternatives.filter(alt => alt.id !== favoriteId);
        
        return favorite ? [favorite, ...others] : alternatives;
    }, [alternatives, favoriteId]);

    const handleCreateNewAlternativeClick = () => {
        navigate(`/${entityName}/project/${cityId}/${projectId}/alternative/create`);
    }

    const handleCompareAlternativesClick = () => {
        setIsCompare(!isCompare);
        onCompareAlternatives();
    }
    
    useEffect(() => {
        if (checkedAlternatives.size <2 || checkedAlternatives.size > 3) {
            setIsNumberOfAlternativesValid(false);
        } else {
            setIsNumberOfAlternativesValid(true);
        }
    }, [checkedAlternatives]);

    return (
        <div className={styles.panelContainer}>
            <div className={styles.container}>
                <div className={styles.addAlternative}>
                    <Button 
                        iconComponent={BuildingsIcon} 
                        icon={"right-side"} 
                        text={t('createAlternative')} 
                        id="addAlternative" 
                        onClick={handleCreateNewAlternativeClick}
                    />
                </div>
                <div className={styles.alternatives}>
                    {sortedAlternatives.map((alternative) => (
                        <AlternativeCard 
                            key={alternative.id} 
                            alternative={alternative} 
                            setFavourite={setFavourite} 
                            isFavourite={alternative.id === favoriteId}
                            isChecked={checkedAlternatives.has(alternative)}
                            onCheck={onCheck}
                            isSelected={alternative.id === selectedAlternativeId}
                            onSelect={() => onAlternativeSelect(alternative)}
                            isBestAlternative={bestAlternative.id === alternative.id}
                        />
                    ))}
                </div>
                <div className={styles.buttons}>
                    <Button 
                        color="skyBlue" 
                        size="fit" 
                        icon={"no"} 
                        text={isCompare ? t('clearComparison') : t('compareAlternatives')} 
                        id="addAlternative" 
                        onClick={handleCompareAlternativesClick} 
                        disabled={!isNumberOfAlternativesValid}
                        textClassName={!isNumberOfAlternativesValid ? styles.grayText : ""}
                    />
                </div>
            </div>
        </div>
    );
}